import CloseIcon from '@mui/icons-material/Close';
import {
  CardMedia,
  CircularProgress,
  InputAdornment,
  Modal,
  TextField,
  FormHelperText,
  IconButton,
  Button,
  Input,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { Dispatch, SetStateAction, useState } from 'react';

import { errorToast } from 'helpers';
import { useAddBrandMutation } from 'service/brands';
import * as Yup from 'yup';

import { showToast } from '../constants/toasts';
import { CommonBody } from '../types/General';
import { generateEncryptedKeyBody } from '../utils/crypto';
import { UploadMedia } from '../utils/mediaUpload';

// props
interface CustomBrandsModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function CustomBrandsModal({ open, onClose, setOpen }: CustomBrandsModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [addBrand] = useAddBrandMutation();

  const formik = useFormik({
    initialValues: {
      name: '',
      image: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Brand name is required field'),
      image: Yup.string().required('Image is required field'),
    }),
    onSubmit: async (values: any) => {
      const body = {
        image: values.image,
        name: values.name,
      };
      formik.resetForm();
      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

        const res = await addBrand({
          body: encryptedBody,
        }).unwrap();
        if (res?.statusCode === 200) {
          showToast('Brand add successfully');
          setOpen(false);
        }
      } catch (error: any) {
        console.error(error);
        errorToast(error?.data?.message);
      }
    },
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (!file) {
      errorToast('No images selected');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/avif'];
    if (!allowedTypes.includes(file.type)) {
      errorToast(`${'Invalid image type:'} ${file.name}`);
      return;
    }

    setLoading(true);
    const res = await UploadMedia(file);
    setLoading(false);

    if (res?.statusCode === 200 && res?.data?.image) {
      formik.setFieldValue('image', res.data.image);
    } else {
      errorToast(`${'Failed to upload:'} ${file.name}`);
    }
  };

  return (
    <Modal
      className="modal brands_modal"
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title hd_3_1">
            <h2>{'Brand'}</h2>
            <div className="btn-close">
              <CloseIcon onClick={() => setOpen(false)} />
            </div>
          </div>

          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="control_group w_50 ">
              <label>{'Brand Name'}</label>
              <TextField
                fullWidth
                hiddenLabel
                name="name"
                placeholder={'Brand name'}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                inputProps={{ maxLength: 50 }}
                sx={{
                  cursor: 'pointer',
                  '& .MuiInputBase-input': { cursor: 'pointer' },
                }}
                // InputProps={{
                //   startAdornment: (
                //     <InputAdornment position="start">
                //       <img src="/images/title.svg" alt="icon" />
                //     </InputAdornment>
                //   ),
                // }}
              />
            </div>

            <div className="control_group w_100">
              <label>Brand Image</label>
              <div className="upload_box_bnr">
                <label>
                  {formik.values.image && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => formik.setFieldValue('image', '')}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={formik.values.image ? formik.values.image : '/static/images/picture.png'}
                      alt="Upload"
                    />
                  </figure>
                  {formik.values.image ? (
                    ''
                  ) : (
                    <IconButton className="uploadIcon">
                      <label htmlFor="icon-button-file-brand">
                        <Button component="span" className="upload_image_btn">
                          <img src="/static/images/edit2_icon.svg" alt="Upload" />
                          <Input
                            sx={{ display: 'none' }}
                            id="icon-button-file-brand"
                            type="file"
                            inputProps={{
                              accept: 'image/png,image/jpeg',
                            }}
                            onChange={handleImageUpload}
                          />
                        </Button>
                      </label>
                    </IconButton>
                  )}
                </label>
              </div>
            </div>

            <div className="btn_flex">
              <button type="submit" className="btnn lg btn_primary w_100">
                {'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
